// --- START OF FILE App.js ---

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { api, BASE_URL } from './apiService';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, LineController, BarController } from 'chart.js';
import * as Sentry from "@sentry/react";

import ErrorBoundary from './components/Common/ErrorBoundary.js';
import Sidebar from './components/Common/Sidebar.js';
import StaffMessageModal from './components/Common/StaffMessageModal.js';
import AuthSection from './components/AuthSection.js';
import DashboardSection from './components/Radio/DashboardSection.js';
import ClientSection from './components/Radio/ClientSection.js';
import AdCampaignSection from './components/Radio/AdCampaignSection.js';
import InvoiceSection from './components/Radio/InvoiceSection.js';
import PaymentSection from './components/Radio/PaymentSection.js';
import ExpenseSection from './components/Radio/ExpenseSection.js';
import AdLogsSection from './components/Radio/AdLogsSection.js';

// Magazine Project Components
import FlareDashboardSection from './components/Magazine/FlareDashboardSection.js';
import MagazineSalesSection from './components/Magazine/MagazineSalesSection.js';
import MagazineClientSection from './components/Magazine/MagazineClientSection.js';
import LiveKioskSection from './components/Attendance/LiveKioskSection.js';
import MonthlyReportSection from './components/Attendance/MonthlyReportSection.js';
import EmployeeRegistrationSection from './components/HR/EmployeeRegistrationSection.js';
import SalaryManagementSection from './components/HR/SalaryManagementSection.js';
import UserManagementSection from './components/Admin/UserManagementSection.js';
import RadioStationManagementSection from './components/Admin/RadioStationManagementSection.js';
import AdminProfileSettingsSection from './components/Admin/AdminProfileSettingsSection.js';
import ProfitAndLossReportSection from './components/Admin/ProfitAndLossReportSection.js';
import MonthlyClosingSection from './components/Admin/MonthlyClosingSection.js';
import BillManagementSection from './components/Billing/BillManagementSection.js';
import PublicInvoiceVerifier from './components/Public/PublicInvoiceVerifier.js';
import MyProfileSection from './components/Common/MyProfileSection.js';

import LoadingScreen from './components/Common/LoadingScreen.js'; 

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, LineController, BarController);

if (window.Sentry) { Sentry.init({ dsn: "YOUR_SENTRY_DSN_URL_HERE", integrations: [ Sentry.browserTracingIntegration(), Sentry.replayIntegration() ], tracesSampleRate: 1.0, replaysSessionSampleRate: 0.1, replaysOnErrorSampleRate: 1.0 }); }

export const AppContext = createContext(null);
export const useAppContext = () => useContext(AppContext);
export const useFirebase = () => ({});

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = location.pathname.split('/')[1] || 'dashboard';
  const [currentProject, setCurrentProject] = useState(localStorage.getItem('currentProject') || 'MIXFM');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    try {
      return window.innerWidth >= 768;
    } catch {
      return true;
    }
  });
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [allStations, setAllStations] = useState([]);
  const [currentStationId, setCurrentStationIdState] = useState(() => {
    const saved = localStorage.getItem('currentStationId');
    if (!saved || saved === 'null' || saved === 'undefined' || saved === 'all') return null;
    return saved;
  });
  const [currentStationLogo, setCurrentStationLogo] = useState('https://placehold.co/100x50/000000/FFFFFF?text=Mix+FM+Logo');
  const [availableStationsForUser, setAvailableStationsForUser] = useState([]);
  const [mainAppLogo, setMainAppLogo] = useState('https://placehold.co/120x60/31206D/FFFFFF?text=Mix+FM+Logo');
  const [appTagline, setAppTagline] = useState('Radio Station Network');
  const [gstEnabled, setGstEnabled] = useState(true);
  const [gstRate, setGstRate] = useState(0.15);
  const [invoiceTerms, setInvoiceTerms] = useState('Payment is due within 15 days.');
  const [bankDetails, setBankDetails] = useState('Bank: HBL\nAccount: 123456789');
  const [route, setRoute] = useState(window.location.pathname);
  const [staffLoginMessage, setStaffLoginMessage] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('staffLoginMessage') || 'null');
      if (saved && typeof saved === 'object') return { isEnabled: !!saved.isEnabled, title: saved.title || '', message: saved.message || '' };
    } catch {}
    return { isEnabled: false, title: '', message: '' };
  });
  const [showStaffMessageModal, setShowStaffMessageModal] = useState(false);
  const pageTitles = { dashboard: "Dashboard", clients: "Clients", adCampaigns: "Ad Campaigns", invoices: "Invoices", payments: "Payment Received", expenses: "Expenses", billManagement: "Bill Management", adLogs: "Ad Logs", liveKiosk: "Live Kiosk", monthlyReport: "Monthly Report", employeeRegistration: "Employee Management", salaryManagement: "Salary Management", profitAndLossReport: "Profit & Loss Report", userManagement: "User Management", radioStationManagement: "Manage Stations", adminProfileSettings: "Admin Settings", monthlyClosing: "Monthly Closing", myProfile: "My Profile" };
  
  const setCurrentStationId = useCallback((stationId) => {
    const next = stationId || null;
    if (next) localStorage.setItem('currentStationId', next);
    else localStorage.removeItem('currentStationId');
    setCurrentStationIdState(next);
  }, []);

  const getRadioStations = useCallback((stations) => {
    return (stations || []).filter(s => {
      const name = (s.name || '').toLowerCase();
      const frequency = (s.frequency || '').trim().toUpperCase();
      const city = (s.city || '').trim().toUpperCase();
      return Boolean(s.id) && frequency && frequency !== 'N/A' && city !== 'N/A' && !name.includes('flare');
    });
  }, []);

  const getFlareBusinessId = useCallback((stations) => {
    const flareBusiness = (stations || []).find(s => {
      const name = (s.name || '').toLowerCase();
      const frequency = (s.frequency || '').trim().toUpperCase();
      return name.includes('flare') || frequency === 'N/A';
    });
    return flareBusiness?.id || null;
  }, []);

  useEffect(() => {
    const loadScript = (src, id, onLoad) => { if (document.getElementById(id)) { if (onLoad) onLoad(); return; } const script = document.createElement('script'); script.src = src; script.id = id; script.async = true; script.onload = onLoad; document.head.appendChild(script); return script; }; let loadedCount = 0; const totalScripts = 4; const onScriptLoad = () => { loadedCount++; if (loadedCount === totalScripts) { loadScript("https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js", "face-api-script", () => setScriptsLoaded(true)); } }; loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', 'jspdf-script', onScriptLoad); loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', 'html2canvas-script', onScriptLoad); loadScript('https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js', 'qrious-script', onScriptLoad); loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.23/jspdf.plugin.autotable.min.js', 'jspdf-autotable-script', onScriptLoad);
    const handlePopState = () => setRoute(window.location.pathname); window.addEventListener('popstate', handlePopState); return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const initApp = async () => {
      if (route.startsWith('/verify')) { setLoading(false); return; }
      
      try {
        // Load initial data from MySQL via API
        const stationsData = await api.get('radio_stations');
        setAllStations(stationsData);
        const radioStations = getRadioStations(stationsData);
        const flareBusinessId = getFlareBusinessId(stationsData);

        // For now, let's simulate a logged-in user or handle it via local storage
        const savedUser = JSON.parse(localStorage.getItem('user'));
        if (savedUser) {
          setUser(savedUser);
          // Load configuration from database
          const config = await api.get('appConfig'); // You'll need to handle this in server.js
          if (config && config.length > 0) {
            const configData = config[0];
            setMainAppLogo(configData.mainLogoUrl || 'https://placehold.co/120x60/31206D/FFFFFF?text=Mix+FM+Logo');
            setAppTagline(configData.tagline || 'Radio Station Network');
            // ... set other config
          }

          const assigned = savedUser.assignedStations || [];
          setAvailableStationsForUser(stationsData.filter(s => assigned.includes(s.id)));
          if (savedUser.role === 'admin') {
            if (currentProject === 'FLARE') setCurrentStationId(flareBusinessId);
            else setCurrentStationId(radioStations.find(s => s.id === currentStationId)?.id || radioStations[0]?.id || null);
          } else if (assigned?.length > 0) {
            if (currentProject === 'FLARE') setCurrentStationId(flareBusinessId || assigned[0]);
            else setCurrentStationId(assigned.includes(currentStationId) ? currentStationId : assigned[0]);
          }
        }
      } catch (err) {
        setError(`Error loading data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (scriptsLoaded) {
      initApp();
    }
  }, [route, scriptsLoaded, currentProject, currentStationId, setCurrentStationId, getRadioStations, getFlareBusinessId]);

  useEffect(() => {
    if (!user) return;
    const assigned = user.assignedStations || [];
    if (assigned.length === 0) return;
    if (!allStations || allStations.length === 0) return;

    setAvailableStationsForUser(allStations.filter(s => assigned.includes(s.id)));

    const flareBusinessId = getFlareBusinessId(allStations);
    const stationFromStorage = currentStationId && assigned.includes(currentStationId) ? currentStationId : null;
    const firstAssigned = assigned[0] || null;
    const nextStationId = stationFromStorage || firstAssigned;

    if (user.role === 'staff') {
      const station = allStations.find(s => s.id === nextStationId);
      const isFlareStation = station && (((station.frequency || '').trim().toUpperCase() === 'N/A') || (station.name || '').toLowerCase().includes('flare'));
      const nextProject = isFlareStation ? 'FLARE' : 'MIXFM';
      if (nextProject !== currentProject) {
        localStorage.setItem('currentProject', nextProject);
        setCurrentProject(nextProject);
      }

      const finalStationId = nextProject === 'FLARE' ? (flareBusinessId || nextStationId) : nextStationId;
      if (finalStationId && finalStationId !== currentStationId) setCurrentStationId(finalStationId);
    }
  }, [user, allStations, currentStationId, currentProject, getFlareBusinessId, setCurrentStationId]);

  useEffect(() => { 
    if (currentProject === 'FLARE') {
      setCurrentStationLogo(mainAppLogo.startsWith('http') ? mainAppLogo : `${BASE_URL}${mainAppLogo}`);
      return;
    }
    if (user) { 
      const selected = allStations.find(s => s.id === currentStationId); 
      const logo = selected?.logoUrl || mainAppLogo || 'https://placehold.co/100x50/000000/FFFFFF?text=Mix+FM+Logo';
      setCurrentStationLogo(logo.startsWith('http') ? logo : `${BASE_URL}${logo}`); 
    } else { 
      setCurrentStationLogo(mainAppLogo.startsWith('http') ? mainAppLogo : `${BASE_URL}${mainAppLogo}`); 
    } 
  }, [currentProject, currentStationId, allStations, user, mainAppLogo]);
  
  const hasAccess = useCallback((pageName) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (pageName === 'myProfile') return true;
    return user.allowedPages?.includes(pageName) || false;
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== 'staff') return;
    if (hasAccess(activeTab)) return;
    const mixfmPages = ['dashboard', 'clients', 'adCampaigns', 'adLogs', 'invoices', 'payments', 'expenses', 'billManagement', 'liveKiosk', 'monthlyReport', 'employeeRegistration', 'salaryManagement'];
    const flarePages = ['dashboard', 'magazineSales', 'clients', 'invoices', 'payments', 'expenses', 'billManagement', 'liveKiosk', 'monthlyReport', 'employeeRegistration', 'salaryManagement'];
    const availablePages = currentProject === 'FLARE' ? flarePages : mixfmPages;
    const firstAllowedPage = availablePages.find(page => hasAccess(page));
    if (!firstAllowedPage) setError("You have no permissions.");
  }, [user, activeTab, hasAccess, currentProject]);

  const handleLogin = (userData, project) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    const assigned = userData?.assignedStations || [];
    if (allStations && allStations.length > 0 && assigned.length > 0) {
      setAvailableStationsForUser(allStations.filter(s => assigned.includes(s.id)));
      const flareBusinessId = getFlareBusinessId(allStations);
      const preferredStationId = assigned.includes(currentStationId) ? currentStationId : assigned[0];
      const preferredStation = allStations.find(s => s.id === preferredStationId);
      const isFlareStation = preferredStation && (((preferredStation.frequency || '').trim().toUpperCase() === 'N/A') || (preferredStation.name || '').toLowerCase().includes('flare'));

      if (userData.role === 'staff') {
        const nextProject = isFlareStation ? 'FLARE' : 'MIXFM';
        localStorage.setItem('currentProject', nextProject);
        setCurrentProject(nextProject);
        setCurrentStationId(nextProject === 'FLARE' ? (flareBusinessId || preferredStationId) : preferredStationId);
        return;
      }
    }

    if (project) {
      localStorage.setItem('currentProject', project);
      setCurrentProject(project);
    }
  };
  
  const handleLogout = useCallback(() => {
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  }, [navigate]);

  if (loading || !scriptsLoaded) return <LoadingScreen logoUrl={mainAppLogo} />;
  
  if (error) return <div className="flex items-center justify-center min-h-screen bg-red-100"><div className="text-xl font-semibold text-red-700">{error}</div></div>;
  if (route.startsWith('/verify')) { return ( <AppContext.Provider value={{ user }}> <PublicInvoiceVerifier /> </AppContext.Provider> ) }

  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <AppContext.Provider value={{ 
          user, allStations, currentStationId, setCurrentStationId, 
          availableStationsForUser, mainAppLogo, setMainAppLogo, appTagline, setAppTagline,    
          gstEnabled, setGstEnabled, gstRate, setGstRate, invoiceTerms, setInvoiceTerms, 
          bankDetails, setBankDetails, activeTab, staffLoginMessage, setStaffLoginMessage,
          currentProject, setCurrentProject: (proj) => { localStorage.setItem('currentProject', proj); setCurrentProject(proj); },
          logout: handleLogout
      }}>
        <StaffMessageModal
            isOpen={showStaffMessageModal}
            title={staffLoginMessage.title}
            message={staffLoginMessage.message}
            onClose={() => {
                setShowStaffMessageModal(false);
                sessionStorage.setItem('staffMessageSeen', 'true');
            }}
        />

        {!user ? (
          <Routes>
            <Route path="/login" element={<AuthSection onLogin={handleLogin} initialMode={currentProject === 'FLARE' ? 'FLARE' : 'MIXFM'} />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        ) : (
          <div className="flex min-h-screen bg-gray-100 text-gray-800">
            <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} user={user} hasAccess={hasAccess} />
             {isSidebarOpen && <div className="fixed inset-0 bg-black opacity-50 z-20 md:hidden" onClick={() => setIsSidebarOpen(false)}></div>}
             <div className="flex-1 flex flex-col overflow-hidden">
                <header className="bg-white shadow-md p-4 flex items-center justify-between md:hidden">
                  <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="text-gray-600">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
                  </button>
                  <h1 className="text-lg font-bold">{pageTitles[activeTab] || 'Menu'}</h1>
                  <div className="w-10">
                    <img src={currentStationLogo} alt="Station Logo" className="h-8 w-auto rounded-md" style={currentProject === 'FLARE' ? { filter: 'brightness(0) invert(1)' } : undefined}/>
                  </div>
                </header>
                <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                    <ErrorBoundary>
                      <Routes>
                        {/* Radio Specific Routes */}
                        <Route path="/dashboard" element={currentProject === 'MIXFM' ? <DashboardSection /> : <FlareDashboardSection />} />
                        <Route path="/clients" element={currentProject === 'MIXFM' ? <ClientSection /> : <MagazineClientSection />} />
                        <Route path="/adCampaigns" element={currentProject === 'MIXFM' ? <AdCampaignSection /> : <Navigate to="/magazineSales" />} />
                        <Route path="/adLogs" element={currentProject === 'MIXFM' ? <AdLogsSection /> : <Navigate to="/magazineSales" />} />
                        
                        {/* Magazine Specific Routes */}
                        <Route path="/magazineSales" element={<MagazineSalesSection />} />

                        {/* Shared/Common Routes */}
                        <Route path="/invoices" element={<InvoiceSection />} />
                        <Route path="/payments" element={<PaymentSection />} />
                        <Route path="/expenses" element={<ExpenseSection />} />
                        <Route path="/billManagement" element={<BillManagementSection />} />
                        <Route path="/liveKiosk" element={<LiveKioskSection />} />
                        <Route path="/monthlyReport" element={<MonthlyReportSection />} />
                        <Route path="/employeeRegistration" element={<EmployeeRegistrationSection />} />
                        <Route path="/salaryManagement" element={<SalaryManagementSection />} />
                        <Route path="/myProfile" element={<MyProfileSection />} />
                        
                        {user.role === 'admin' && <Route path="/profitAndLossReport" element={<ProfitAndLossReportSection />} />}
                        {user.role === 'admin' && <Route path="/userManagement" element={<UserManagementSection />} />}
                        {user.role === 'admin' && <Route path="/radioStationManagement" element={<RadioStationManagementSection />} />}
                        {user.role === 'admin' && <Route path="/adminProfileSettings" element={<AdminProfileSettingsSection />} />}
                        {user.role === 'admin' && <Route path="/monthlyClosing" element={<MonthlyClosingSection />} />}
                        
                        <Route path="*" element={<Navigate to={currentProject === 'MIXFM' ? "/dashboard" : "/dashboard"} />} />
                      </Routes>
                    </ErrorBoundary>
                  </main>
             </div>
          </div>
        )}
      </AppContext.Provider>
    </>
  );
}

export default App;
